import { BLOCK_LAYOUTS, type BlockLayout } from "../lib/segment-types";

/**
 * Where the picture sits relative to the words, chosen by pointing at it.
 *
 * This was a dropdown inside the settings drawer, which is two clicks away
 * from the card and names arrangements rather than showing them. Position is
 * a spatial decision: the fastest way to say "put the picture on the other
 * side" is to point at a diagram of it.
 */

/** A miniature of the arrangement — picture block and text lines. */
function Glyph({ layout }: { layout: BlockLayout }) {
  const pic = <span className="nl-lay-pic" aria-hidden />;
  const lines = (
    <span className="nl-lay-lines" aria-hidden>
      <i /><i /><i />
    </span>
  );
  if (layout === "text") return <span className="nl-lay-fig is-col">{lines}</span>;
  if (layout === "overlay") return <span className="nl-lay-fig is-overlay">{pic}{lines}</span>;
  if (layout === "stacked") return <span className="nl-lay-fig is-col">{pic}{lines}</span>;
  if (layout === "image-below") return <span className="nl-lay-fig is-col">{lines}{pic}</span>;
  if (layout === "image-end") return <span className="nl-lay-fig is-row">{lines}{pic}</span>;
  return <span className="nl-lay-fig is-row">{pic}{lines}</span>;
}

export function LayoutPicker({
  value, disabled, options, onChange,
}: {
  value: BlockLayout;
  disabled?: boolean;
  /** Narrow the set — a link has no use for the overlay, for instance. */
  options?: readonly BlockLayout[];
  onChange: (next: BlockLayout) => void;
}) {
  const shown = options
    ? BLOCK_LAYOUTS.filter((l) => options.includes(l.value))
    : BLOCK_LAYOUTS;
  return (
    <div className="nl-lay" role="radiogroup" aria-label="Where the picture sits">
      {shown.map((l) => (
        <button
          key={l.value}
          type="button"
          role="radio"
          aria-checked={value === l.value}
          aria-label={`${l.label} — ${l.hint}`}
          title={l.hint}
          className="nl-lay-opt"
          disabled={disabled}
          onClick={() => onChange(l.value)}
        >
          <Glyph layout={l.value} />
          <span className="nl-lay-name">{l.label}</span>
        </button>
      ))}
    </div>
  );
}
